import React, { useEffect, useState } from "react"

import * as Sentry from "@sentry/react"
import { AlertCircle, ArrowLeft, GitCompare } from "lucide-react"
import { useNavigate, useParams } from "react-router-dom"

import ApplicantActions from "@/components/applicants/ApplicantActions"
import ApplicantDetailsMain from "@/components/applicants/ApplicantDetailsMain"
import DocumentVerification from "@/components/applicants/DocumentVerification"
import EditApplicantDialog from "@/components/applicants/EditApplicantDialog"
import ScheduleDialog from "@/components/applicants/ScheduleDialog"
import StatusPanel from "@/components/applicants/StatusPanel"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useApplicationDetails } from "@/hooks/useApplicationDetails"

import type { Tables } from "../../database.types"
import DashboardLayout from "../layouts/DashboardLayout"

const { logger } = Sentry
const sentryVerbose = import.meta.env.VITE_SENTRY_VERBOSE_UI === "true"

const ApplicationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { applicationDetails, isLoading, isError, error, refetch } = useApplicationDetails(id)

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false)
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false)
  const [sessionType, setSessionType] = useState<"orientation" | "vehicle_collection">(
    "orientation",
  )
  const [isRescheduling, setIsRescheduling] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState<Tables<"documents"> | null>(null)

  useEffect(() => {
    Sentry.startSpan(
      {
        op: "page.load",
        name: "Application Details Page Load",
      },
      (span) => {
        span.setAttribute("application_id", id ?? "unknown")
        if (sentryVerbose) {
          logger.info("Application details page loaded", { applicationId: id })
        }
      },
    )
  }, [id])

  useEffect(() => {
    if (isError && error) {
      Sentry.captureException(error)
      if (sentryVerbose) {
        logger.error("Failed to load application details", {
          applicationId: id,
          error,
        })
      }
    }
  }, [isError, error, id])

  const handleRescheduleOrientation = () => {
    setSessionType("orientation")
    setIsRescheduling(true)
    setIsScheduleDialogOpen(true)
  }

  const handleRescheduleVehicleCollection = () => {
    setSessionType("vehicle_collection")
    setIsRescheduling(true)
    setIsScheduleDialogOpen(true)
  }

  const handleScheduleDialogClose = () => {
    setIsScheduleDialogOpen(false)
    setIsRescheduling(false)
  }

  const handleViewDocument = (document: Tables<"documents">) => {
    Sentry.addBreadcrumb({
      category: "ui",
      level: "info",
      message: "Document viewed",
      data: { applicationId: id, documentName: document.type },
    })

    setSelectedDocument(document)
    setIsDocumentDialogOpen(true)
  }

  const handleCompareClick = () => {
    if (applicationDetails?.application_id) {
      navigate(`/applications/compare?appA=${applicationDetails.application_id}`)
    }
  }

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="ghost"
          className="flex items-center gap-2"
          onClick={() => navigate("/applications")}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Applications
        </Button>
        <div className="flex items-center gap-2">
          {applicationDetails?.application_id && (
            <Button
              variant="outline"
              onClick={handleCompareClick}
              className="flex items-center gap-2"
            >
              <GitCompare className="h-4 w-4" />
              Compare
            </Button>
          )}
          {id && applicationDetails?.current_stage && (
            <ApplicantActions
              navigateTo="/applications"
              applicationId={id}
              currentStage={applicationDetails?.current_stage}
              hideBackButton={true}
            />
          )}
        </div>
      </div>
      {isLoading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="col-span-2">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <Skeleton className="h-8 w-1/3 mb-4" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-full" />
                </div>
              </div>
            </div>
            <div className="col-span-1">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <Skeleton className="h-8 w-1/2 mb-4" />
                <Skeleton className="h-6 w-full mb-2" />
                <Skeleton className="h-6 w-2/3" />
              </div>
            </div>
          </div>
        </div>
      ) : isError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : "Failed to load application details"}
          </AlertDescription>
        </Alert>
      ) : !applicationDetails ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No Data Found</AlertTitle>
          <AlertDescription>No application found with ID: {id}</AlertDescription>
        </Alert>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="col-span-2">
            <ApplicantDetailsMain
              application={applicationDetails}
              onViewDocument={handleViewDocument}
              onEditApplicant={() => setIsEditDialogOpen(true)}
              onScheduleOrientation={() => {
                setSessionType("orientation")
                setIsRescheduling(false)
                setIsScheduleDialogOpen(true)
              }}
              onScheduleVehicleCollection={() => {
                setSessionType("vehicle_collection")
                setIsRescheduling(false)
                setIsScheduleDialogOpen(true)
              }}
              onRescheduleOrientation={handleRescheduleOrientation}
              onRescheduleVehicleCollection={handleRescheduleVehicleCollection}
            />
          </div>
          <div className="col-span-1">
            <StatusPanel
              status={applicationDetails.status}
              segment={applicationDetails.current_segment}
              current_stage={applicationDetails.current_stage}
              stage_reason={applicationDetails.stage_reason}
              stage_note={applicationDetails.stage_note}
              personAirtableId={applicationDetails.person?.person_id}
              primary_person_id={applicationDetails.user?.primary_person?.person ?? null}
              personId={applicationDetails.person?.id}
              onEditClick={() => setIsEditDialogOpen(true)}
              name={applicationDetails.name}
              id={id}
              applicationId={applicationDetails.application_id}
              allowedStages={applicationDetails.allowed_stages || []}
              contract_salary={applicationDetails.contract_salary ?? null}
              onContractSalaryChange={() => {
                refetch()
              }}
            />
          </div>
        </div>
      )}
      {applicationDetails && (
        <>
          <EditApplicantDialog
            isOpen={isEditDialogOpen}
            onClose={() => setIsEditDialogOpen(false)}
            application={applicationDetails}
          />

          {selectedDocument && (
            <DocumentVerification
              isOpen={isDocumentDialogOpen}
              onClose={() => setIsDocumentDialogOpen(false)}
              document={selectedDocument}
            />
          )}

          <ScheduleDialog
            isOpen={isScheduleDialogOpen}
            onClose={handleScheduleDialogClose}
            applicationUUID={applicationDetails.id}
            applicationId={applicationDetails.application_id || ""}
            jobId={applicationDetails.job?.id || ""}
            sessionType={sessionType}
            isRescheduling={isRescheduling}
          />
        </>
      )}
    </DashboardLayout>
  )
}

export default ApplicationDetails
